/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import Table, { type ColumnsType } from "antd/lib/table";
import React, { useEffect, useState } from "react";
import { type FileSharingType } from "../redux/slices/midservice/midserviceTypes";
import { Button, Col, message, Row, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { API } from "../api/Api";
import { timestampToReadable } from "../utils/time";
import { toNumber } from "lodash";
import { type TableRowSelection } from "antd/lib/table/interface";
import Link from "antd/lib/typography/Link";
import { LoadingOutlined } from "@ant-design/icons";
import ProgressMessage from "../components/ProgressMessage";
import { beautifyFilename, renameFile } from "../utils/filenames";

const FileSharingPage: React.FC = () => {
  return <FileSharingTable />;
};

const FileSharingTable: React.FC = () => {
  const [allFiles, setAllFiles] = useState<FileSharingType[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [update, setUpdate] = useState<boolean>(false);

  useEffect(() => {
    async function getFiles(): Promise<void> {
      try {
        const { data } = await API.getExchangeFiles();
        setAllFiles(data.files);
      } catch (error) {
        console.error("Get files error", error);
      }
    }

    getFiles();
  }, [update]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]): void => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<FileSharingType> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  async function uploadFiles(): Promise<void> {
    const input = document.createElement("input");
    const messageKey = "upload";
    input.type = "file";
    input.multiple = true;
    input.onchange = async (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) {
        let size = 0;
        for (const file of Array.from(files)) {
          size += file.size;
        }
        if (size > 99 * 1024 * 1024) {
          // limit 99 MB
          console.error("The upload limit has been exceeded (maximum 99 MB)");
          message.error({
            key: messageKey,
            content: "Превышен лимит загрузки (максимум 99 МБ)",
            duration: 3,
          });
          return;
        }
        message.open({
          key: messageKey,
          content: <ProgressMessage progress={0} type={messageKey} />,
          duration: 0,
          icon: null,
        });

        const formData = new FormData();
        for (const file of Array.from(files)) {
          let uniqueFilename = file.name;
          if (allFiles.find((f) => f.filename === file.name)) {
            uniqueFilename = `${file.name}_${Date.now()}`;
          }
          const newFile = renameFile(file, uniqueFilename);
          formData.append("files", newFile);
          setAllFiles((value) => [
            {
              filename: uniqueFilename,
              size: toNumber((file.size / (1024 * 1024)).toFixed(2)),
              createdAt: "loading",
            },
            ...value,
          ]);
        }
        try {
          await API.uploadExchangeFiles(formData, (progress: number) => {
            message.open({
              key: messageKey,
              content: (
                <ProgressMessage progress={progress} type={messageKey} />
              ),
              duration: 0,
              icon: null,
            });
          });
          message.success({
            key: messageKey,
            content: "Файлы успешно загружены",
            duration: 2,
          });
          setUpdate((value) => !value);
        } catch (error) {
          console.error("Upload files error", error);
          message.error({
            key: messageKey,
            content: "Произошла ошибка при загрузке файлов",
            duration: 2,
          });
        }
      }
    };
    input.click();
  }

  async function deleteFiles(): Promise<void> {
    try {
      const filenames = selectedRowKeys.map((file) => file.toLocaleString());
      await API.deleteExchangeFiles(filenames);
      setUpdate((value) => !value);
      setSelectedRowKeys([]);
      message.success("Файлы успешно удалены");
    } catch (error) {
      console.error("Delete files error", error);
    }
  }

  const CopyToClipboard = (e: any, filename: string): void => {
    e.preventDefault();
    const linkToDownload = `${
      process.env.REACT_APP_HTTP_PROTOCOL ?? "http"
    }://${
      process.env.REACT_APP_URL ?? "localhost:3000"
    }/download-exchange-file/${filename}`;
    navigator.clipboard.writeText(linkToDownload);
    message.info("Ссылка скопирована в буфер обмена", 3);
  };

  const handleDownload = async (
    e: { preventDefault: () => void },
    filename: string,
  ): Promise<void> => {
    e.preventDefault();
    const messageKey = "download";
    message.open({
      key: messageKey,
      content: <ProgressMessage progress={0} type={messageKey} />,
      duration: 0,
      icon: null,
    });

    try {
      const response = await API.downloadExchangeFile(filename, (progress) => {
        message.open({
          key: messageKey,
          content: <ProgressMessage progress={progress} type={messageKey} />,
          duration: 0,
          icon: null,
        });
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", beautifyFilename(filename));
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success({
        key: messageKey,
        content: "Файл успешно загружен",
        duration: 2,
      });
    } catch (error) {
      console.error("Download file error", error);
      message.error({
        key: messageKey,
        content: "Ошибка при скачивании файла",
        duration: 2,
      });
    }
  };

  const columns: ColumnsType<FileSharingType> = [
    {
      title: "Название",
      dataIndex: "filename",
      key: "filename",
      render: (filename) => (
        <Link
          onClick={async (e) => {
            await handleDownload(e, filename);
          }}
          onContextMenu={(e) => {
            CopyToClipboard(e, filename);
          }}
        >
          {beautifyFilename(filename)}
        </Link>
      ),
    },
    {
      title: "Размер",
      dataIndex: "size",
      key: "size",
      render(value: string) {
        return `${value} МБ`;
      },
    },
    {
      title: "Дата загрузки",
      dataIndex: "createdAt",
      key: "createdAt",
      sortOrder: "descend",
      sorter: (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text) => {
        if (text === "loading") {
          return <Spin indicator={<LoadingOutlined spin />} size="small" />;
        }
        return timestampToReadable(text);
      },
    },
    {
      title: "Операции",
      key: "actionsOnFile",
      fixed: "right",
      width: 100,
    },
  ];

  const getTableDataSource = (): any[] => {
    return allFiles.map((item: FileSharingType) => ({
      ...item,
      key: item.filename,
    }));
  };

  return (
    <>
      <Row style={{ marginBottom: "1rem", gap: "1rem" }}>
        <Col flex="auto">
          <Title level={3}>Обмен файлами</Title>
        </Col>
        {selectedRowKeys.length > 0 && (
          <Col flex="right">
            <Button type="primary" danger onClick={deleteFiles}>
              Удалить выбранные
            </Button>
          </Col>
        )}
        <Col flex="right">
          <Button type="primary" onClick={uploadFiles}>
            Загрузить файл
          </Button>
        </Col>
      </Row>
      <Table
        rowSelection={rowSelection}
        size="small"
        onRow={(file, index) => {
          if (typeof index !== "undefined") {
            const attr: any = {
              index,
              file,
            };
            return attr as React.HTMLAttributes<any>;
          }
          return {} satisfies React.HTMLAttributes<any>;
        }}
        scroll={{ x: true }}
        columns={columns}
        dataSource={getTableDataSource()}
      />
    </>
  );
};

export default FileSharingPage;
